import { UpgradeSubscriptionModal } from '@components'

import { Divider, Flex, Spacing, Text, useModal } from '@billy/design-system'
import { Fragment, type ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { ModalId } from '../../../../../../../../enums/modalId'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useComponentId, useFormContext } from '../../../../../../../../hooks'
import { AutomaticPaymentRemindersType } from '../../../../enums/automaticPaymentRemindersType'
import type { PaymentRemindersDetails } from '../../../../types/paymentReminders/paymentRemindersDetails'
import type { PaymentsAndRemindersSettingsFormSchema } from '../../types/paymentsAndRemindersSettingsFormSchema'
import { AutomaticPaymentRemindersModal } from '../AutomaticPaymentRemindersModal'
import { AutomaticPaymentRemindersRow } from './elements/AutomaticPaymentRemindersRow'
import { AutomaticPaymentRemindersStartDate } from './elements/AutomaticPaymentRemindersStartDate'
import * as Styled from './styles'

const automaticPaymentRemindersTypes = Object.values(AutomaticPaymentRemindersType)

export const AutomaticPaymentReminders = (): ReactElement => {
  const { t } = useTranslation()
  const [editedType, setEditedType] = useState(AutomaticPaymentRemindersType.OnDueDate)
  const { organization } = useUserOrganization()
  const { getValues, setValue } = useFormContext<PaymentsAndRemindersSettingsFormSchema>()
  const remindersModalId = useComponentId(ModalId.AutomaticPaymentRemindersModal)
  const upgradeSubscriptionModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { close: closeRemindersModal, open: openRemindersModal } = useModal(remindersModalId)
  const { open: openUpgradeSubscriptionModal } = useModal(upgradeSubscriptionModalId)

  const hasPremiumSubscriptionPlan = organization?.subscriptionPlan === SubscriptionPlan.Premium

  const handleReminderEdit = useCallback(
    (paymentRemindersType: AutomaticPaymentRemindersType) => {
      if (!hasPremiumSubscriptionPlan) {
        openUpgradeSubscriptionModal()
        return
      }

      setEditedType(paymentRemindersType)
      openRemindersModal()
    },
    [hasPremiumSubscriptionPlan, openRemindersModal, openUpgradeSubscriptionModal],
  )

  // Applying the edit modal also switches the reminder on.
  const handleRemindersModalSubmit = useCallback(
    (paymentRemindersDetails: PaymentRemindersDetails) => {
      setValue(
        `automaticPaymentRemindersSettings.${editedType}`,
        { ...paymentRemindersDetails, active: true },
        { shouldDirty: true },
      )
      closeRemindersModal()
    },
    [closeRemindersModal, editedType, setValue],
  )

  return (
    <Flex flexDirection="column" gap={Spacing.S16}>
      <Flex flexDirection="column" gap={Spacing.S4}>
        <Text weight="medium">{t('settings.organization.external_invoice.automatic_payment_reminders.title')}</Text>
        <Text colorVariant="secondary">
          {t('settings.organization.external_invoice.automatic_payment_reminders.description')}
        </Text>
      </Flex>
      <Styled.AutomaticPaymentRemindersRowsWrapper>
        {automaticPaymentRemindersTypes.map((paymentRemindersType, index) => (
          <Fragment key={paymentRemindersType}>
            {index > 0 && <Divider />}
            <AutomaticPaymentRemindersRow
              hasPremiumSubscriptionPlan={hasPremiumSubscriptionPlan}
              onEdit={handleReminderEdit}
              onUpgradeRequired={openUpgradeSubscriptionModal}
              paymentRemindersType={paymentRemindersType}
            />
          </Fragment>
        ))}
      </Styled.AutomaticPaymentRemindersRowsWrapper>
      <AutomaticPaymentRemindersStartDate />
      <AutomaticPaymentRemindersModal
        defaultValues={getValues(`automaticPaymentRemindersSettings.${editedType}`)}
        id={remindersModalId}
        onSubmit={handleRemindersModalSubmit}
        variant={editedType}
      />
      <UpgradeSubscriptionModal
        id={upgradeSubscriptionModalId}
        trackingContext={TrackingContext.AutomaticPaymentReminders}
      />
    </Flex>
  )
}
